import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { checkServerReachable } from 'api/networkError';
import ErrorIcon from 'assets/Error';
import { NoResponseRequestInfo } from 'types/api';

import './NetworkErrorContent.styles.scss';

type Reachability = 'checking' | 'reachable' | 'unreachable';

interface NetworkErrorContentProps {
	code: string;
	request?: NoResponseRequestInfo;
	/** 호출처의 폼이 닫히지 않아 입력값이 남아 있을 때만 true — 거짓 안내 방지. */
	keepsInput?: boolean;
	/** 있으면 "다시 시도" 버튼이 이 콜백을 부른 뒤 모달을 닫는다. */
	onRetry?: () => void;
	onClose?: () => void;
}

const TITLE_KEY: Record<Reachability, string> = {
	checking: 'net_err_title_pending',
	reachable: 'net_err_title_server_ok',
	unreachable: 'net_err_title_server_down',
};

const BODY_KEY: Record<Reachability, string | undefined> = {
	checking: undefined,
	reachable: 'net_err_body_server_ok',
	unreachable: 'net_err_body_server_down',
};

const STATUS_KEY: Record<Reachability, string> = {
	checking: 'net_err_checking',
	reachable: 'net_err_server_check_ok',
	unreachable: 'net_err_server_check_down',
};

/**
 * 서버 응답 없이 끝난 요청(연결 끊김·타임아웃) 전용 안내. 본문 없는 GET 으로
 * 서버 도달 여부를 직접 확인해, "서버 장애"와 "이 PC 와 서버 사이(프록시·VPN·
 * 보안 장비)에서 끊김"을 제목에서 갈라 말한다. 2026-08-26 사내망 프록시가
 * PUT 본문을 삼켜 nginx 408 → 화면엔 ERR_NETWORK 만 뜨던 사고의 후속.
 */
function NetworkErrorContent({
	code,
	request,
	keepsInput,
	onRetry,
	onClose,
}: NetworkErrorContentProps): JSX.Element {
	const { t } = useTranslation('common');
	const [reachability, setReachability] = useState<Reachability>('checking');

	useEffect(() => {
		let cancelled = false;
		const probe = async (): Promise<void> => {
			const reachable = await checkServerReachable();
			if (!cancelled) {
				setReachability(reachable ? 'reachable' : 'unreachable');
			}
		};
		void probe();
		return (): void => {
			cancelled = true;
		};
	}, []);

	const handleRetry = (): void => {
		onRetry?.();
		onClose?.();
	};

	const requestLine = [request?.method, request?.url].filter(Boolean).join(' ');
	const details = [
		code,
		requestLine || undefined,
		request?.elapsedMs
			? t('net_err_no_response_for', {
					seconds: Math.round(request.elapsedMs / 1000),
				})
			: undefined,
		reachability === 'checking' ? undefined : t(STATUS_KEY[reachability]),
		request?.occurredAt
			? new Date(request.occurredAt).toLocaleString()
			: undefined,
	].filter(Boolean);

	const bodyKey = BODY_KEY[reachability];

	return (
		<section className="network-error" data-testid="network-error-content">
			<header className="network-error__summary">
				<div className="network-error__icon">
					<ErrorIcon />
				</div>
				<div className="network-error__text">
					<h2 className="network-error__title">{t(TITLE_KEY[reachability])}</h2>
					{bodyKey && <p className="network-error__body">{t(bodyKey)}</p>}
					{keepsInput && (
						<p className="network-error__keeps-input">{t('net_err_keeps_input')}</p>
					)}
				</div>
			</header>

			<div
				className={`network-error__status network-error__status--${reachability}`}
				data-testid="network-error-status"
			>
				{t(STATUS_KEY[reachability])}
			</div>

			<div className="network-error__actions">
				<Button type="default" onClick={onClose} data-testid="network-error-close">
					{t('close')}
				</Button>
				{onRetry && (
					<Button
						type="primary"
						onClick={handleRetry}
						data-testid="network-error-retry"
					>
						{t('retry')}
					</Button>
				)}
			</div>

			<details className="network-error__details" open>
				<summary>{t('technical_details')}</summary>
				<code data-testid="network-error-details">{details.join(' · ')}</code>
			</details>
		</section>
	);
}

NetworkErrorContent.defaultProps = {
	request: undefined,
	keepsInput: false,
	onRetry: undefined,
	onClose: undefined,
};

export default NetworkErrorContent;
